/* global google */
import React from 'react';
import { GoogleMap, Polygon } from '@react-google-maps/api';
import { Typography } from '@mui/material';
import toast from '../../../utils/toast';

import type { TenantRoute, TenantRoutePoint } from '../../../types/TenantRouteType';
import { routeColor } from '../../../utils/tenantRoutes';

type PropsType = {
    height?: number | string,
    routes: TenantRoute[],
    selectedId?: number | null,
    /** Zoom the map to this route when it changes or first becomes available */
    fitToId?: number | null,
    onSelect?: (id: number) => void,
    onPointsChange: (id: number, points: TenantRoutePoint[]) => void
};

export type EditableRouteMapHandle = {
    commitSelected: () => void
};

const defaultCenter = { lat: 63, lng: 16 };
const defaultZoom = 5;
const mapOptions = {
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    clickableIcons: false,
    minZoom: 4,
    maxZoom: 20
};

// Same limit as the API (shared-services/tenant-routes.ts MIN_POINTS)
const MIN_POINTS = 4;

function pathToPoints(path: google.maps.MVCArray<google.maps.LatLng>): TenantRoutePoint[] {
    const points: TenantRoutePoint[] = [];
    for (let i = 0; i < path.getLength(); i += 1) {
        const point = path.getAt(i);
        points.push({ lat: point.lat(), lng: point.lng() });
    }
    return points;
}

const EditableRouteMap = React.forwardRef<EditableRouteMapHandle, PropsType>((props, ref) => {
    const {
        height = 360,
        routes,
        selectedId,
        fitToId,
        onSelect,
        onPointsChange
    } = props;

    const polygonRefs = React.useRef<Record<number, google.maps.Polygon>>({});
    const mapRef = React.useRef<google.maps.Map | null>(null);
    const [mapReady, setMapReady] = React.useState(false);
    const fitRoute = routes.find((route) => route.id === fitToId);
    const fitRouteRef = React.useRef(fitRoute);
    fitRouteRef.current = fitRoute;
    const hasFitRoute = !!fitRoute;

    // Depends on the id (not the points) so editing the polygon doesn't keep re-zooming
    React.useEffect(() => {
        const map = mapRef.current;
        const route = fitRouteRef.current;
        if (!mapReady || !map || !route || route.points.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        route.points.forEach((point) => bounds.extend(point));
        map.fitBounds(bounds);
    }, [mapReady, fitToId, hasFitRoute]);
    const selectedIdRef = React.useRef(selectedId);
    selectedIdRef.current = selectedId;

    const commitPath = React.useCallback((id: number) => {
        const polygon = polygonRefs.current[id];
        if (!polygon) return;
        onPointsChange(id, pathToPoints(polygon.getPath()));
    }, [onPointsChange]);

    const commitSelected = React.useCallback(() => {
        const id = selectedIdRef.current;
        if (id != null) {
            commitPath(id);
        }
    }, [commitPath]);

    React.useImperativeHandle(ref, () => ({ commitSelected }), [commitSelected]);

    // Right-click a point of the selected polygon to remove it (same in all route/area editors).
    // Kept in a ref because the listener is attached once in onLoad.
    const removeVertexRef = React.useRef<(id: number, e: google.maps.PolyMouseEvent) => void>(() => undefined);
    removeVertexRef.current = (id, e) => {
        const polygon = polygonRefs.current[id];
        if (id !== selectedIdRef.current || !polygon || e.vertex == null) return;
        const path = polygon.getPath();
        if (path.getLength() <= MIN_POINTS) {
            toast(`Ett område behöver minst ${MIN_POINTS} punkter.`);
            return;
        }
        path.removeAt(e.vertex);
        commitPath(id);
    };

    return (
        <>
            <GoogleMap
                mapContainerStyle={{ height, width: '100%', overflow: 'hidden' }}
                center={defaultCenter}
                zoom={defaultZoom}
                options={mapOptions}
                onLoad={(map) => {
                    mapRef.current = map;
                    setMapReady(true);
                }}
                onUnmount={() => {
                    mapRef.current = null;
                    setMapReady(false);
                }}
            >
                {routes.map((route, index) => {
                    const selected = route.id === selectedId;
                    const color = routeColor(index);
                    return (
                        <Polygon
                            key={route.id}
                            paths={route.points}
                            options={{
                                strokeColor: color,
                                fillColor: color,
                                fillOpacity: selected ? 0.28 : 0.12,
                                strokeWeight: selected ? 2.5 : 1.5,
                                strokeOpacity: 1,
                                clickable: true,
                                editable: selected,
                                draggable: selected,
                                zIndex: selected ? 2 : 1
                            }}
                            onLoad={(polygon) => {
                                polygonRefs.current[route.id] = polygon;
                                // 'contextmenu' (not the deprecated 'rightclick') also covers ctrl-/two-finger click on macOS
                                polygon.addListener('contextmenu', (e: google.maps.PolyMouseEvent) => {
                                    removeVertexRef.current(route.id, e);
                                });
                            }}
                            onUnmount={() => {
                                delete polygonRefs.current[route.id];
                            }}
                            onClick={() => {
                                commitSelected();
                                onSelect?.(route.id);
                            }}
                            onMouseUp={() => {
                                if (selected) {
                                    commitPath(route.id);
                                }
                            }}
                            onDragEnd={() => {
                                if (selected) {
                                    commitPath(route.id);
                                }
                            }}
                        />
                    );
                })}
            </GoogleMap>
            <Typography variant="caption">
                Klicka på en rutt för att välja den. Dra i ytan för att flytta hela rutten, eller i punkterna
                för att forma den. Högerklicka på en punkt för att ta bort den.
            </Typography>
        </>
    );
});

EditableRouteMap.displayName = 'EditableRouteMap';

export default EditableRouteMap;
